<template>
  <div v-if="hasError" class="error-boundary">
    <div class="error-boundary__card">
      <div class="error-boundary__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 9v4" />
          <path d="M12 17h.01" />
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        </svg>
      </div>
      <h2>Something went wrong on this page</h2>
      <p>
        We've logged the details and our team has been notified. You can retry the
        page or report this to us with a note about what you were doing.
      </p>
      <p v-if="correlationId" class="error-boundary__ref">
        Reference:
        <code>{{ correlationId }}</code>
      </p>
      <div class="error-boundary__actions">
        <button class="btn btn--ghost" @click="reload">Reload page</button>
        <button class="btn btn--primary" @click="openReport">Report this issue</button>
      </div>
    </div>
  </div>
  <slot v-else />
</template>

<script setup>
import { onErrorCaptured, ref } from 'vue'
import { reportError } from '@/utils/errorReporter'
import { openSupportWidget } from '@/utils/supportWidgetBus'

const hasError = ref(false)
const correlationId = ref('')

onErrorCaptured((err) => {
  hasError.value = true
  correlationId.value = Math.random().toString(36).slice(2, 10)
  reportError(err, { source: 'error-boundary', context: { boundary: true } })
  return false
})

function reload() {
  hasError.value = false
  window.location.reload()
}

function openReport() {
  openSupportWidget({ category: 'bug', prefillMessage: `Auto-captured error. Reference: ${correlationId.value}` })
}
</script>

<style scoped>
.error-boundary {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  padding: 40px 20px;
}
.error-boundary__card {
  max-width: 520px;
  width: 100%;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 32px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(15, 23, 42, 0.06);
}
.error-boundary__icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fef3c7;
  color: #b45309;
}
.error-boundary__icon svg { width: 28px; height: 28px; }
.error-boundary h2 { margin: 0 0 8px; font-size: 18px; color: #0f172a; }
.error-boundary p { margin: 0 0 12px; color: #475569; font-size: 14px; line-height: 1.5; }
.error-boundary__ref { font-size: 12px; color: #64748b; }
.error-boundary__ref code {
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: ui-monospace, monospace;
}
.error-boundary__actions {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
}
.btn {
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #0f172a;
  font-size: 14px;
  cursor: pointer;
}
.btn--primary { background: #0f172a; color: #fff; border-color: #0f172a; }
.btn:hover { opacity: 0.9; }
</style>
